import type { ImplicationRule } from '../api/contracts'

export interface ScopedPermission {
  actionId: string
  targetResourceId: string | null
  targetResourceTypeId: string | null
  resultResourceTypeId: string | null
}

export const scopedPermissionKey = (scope: ScopedPermission) =>
  [
    scope.actionId,
    scope.targetResourceId ?? '',
    scope.targetResourceTypeId ?? '',
    scope.resultResourceTypeId ?? '',
  ].join('|')

function applyRule(rule: ImplicationRule, scope: ScopedPermission): ScopedPermission | null {
  if (
    rule.from_act_id !== scope.actionId ||
    (rule.from_tgt_type !== null && rule.from_tgt_type !== scope.targetResourceTypeId) ||
    (rule.from_res_type !== null && rule.from_res_type !== scope.resultResourceTypeId)
  )
    return null

  if (rule.bind_source === 'from_result') return null // Result identities exist only after creation.
  if (rule.bind_source === 'preserve' && rule.bind_target === 'preserve') {
    if (
      (rule.to_tgt_type !== null && rule.to_tgt_type !== scope.targetResourceTypeId) ||
      (rule.to_res_type !== null && rule.to_res_type !== scope.resultResourceTypeId)
    )
      return null
    return { ...scope, actionId: rule.to_act_id }
  }
  if (rule.bind_source === 'preserve' && rule.bind_target === 'none') {
    return {
      actionId: rule.to_act_id,
      targetResourceId: null,
      targetResourceTypeId: null,
      resultResourceTypeId: rule.to_res_type,
    }
  }
  if (
    rule.bind_source === 'from_target' &&
    rule.bind_target === 'to_target' &&
    scope.targetResourceId !== null &&
    rule.to_tgt_type === scope.targetResourceTypeId
  ) {
    return {
      actionId: rule.to_act_id,
      targetResourceId: scope.targetResourceId,
      targetResourceTypeId: rule.to_tgt_type,
      resultResourceTypeId: rule.to_res_type,
    }
  }
  return null
}

export function getScopedClosure(
  rules: ImplicationRule[],
  source: ScopedPermission
): Map<string, ScopedPermission> {
  const result = new Map<string, ScopedPermission>()
  const stack = [source]
  while (stack.length) {
    const current = stack.pop()!
    const key = scopedPermissionKey(current)
    if (result.has(key)) continue
    result.set(key, current)
    for (const rule of rules) {
      const implied = applyRule(rule, current)
      if (implied) stack.push(implied)
    }
  }
  return result
}

export function isScopedPermissionEffective(
  rules: ImplicationRule[],
  target: ScopedPermission,
  explicit: Iterable<ScopedPermission>
): boolean {
  const key = scopedPermissionKey(target)
  for (const source of explicit) if (getScopedClosure(rules, source).has(key)) return true
  return false
}

export function getScopedToggleChanges(
  rules: ImplicationRule[],
  target: ScopedPermission,
  explicit: ReadonlyMap<string, ScopedPermission>,
  allowed?: ReadonlySet<string>
): { add: Map<string, ScopedPermission>; remove: Set<string> } {
  const targetKey = scopedPermissionKey(target)
  const add = new Map<string, ScopedPermission>()
  const remove = new Set<string>()
  if (!isScopedPermissionEffective(rules, target, explicit.values())) {
    add.set(targetKey, target)
    return { add, remove }
  }

  for (const [key, source] of explicit) {
    if (
      getScopedClosure(rules, source).has(targetKey) &&
      (!allowed || allowed.has(source.actionId))
    )
      remove.add(key)
  }
  const remaining = new Map([...explicit].filter(([key]) => !remove.has(key)))
  const before = new Map<string, ScopedPermission>()
  for (const source of explicit.values())
    for (const [key, implied] of getScopedClosure(rules, source)) before.set(key, implied)

  // Preserve permissions that were checked before the toggle, except the
  // unchecked permission and anything that would imply it again.
  for (const [key, permission] of before) {
    if (key === targetKey || (allowed && !allowed.has(permission.actionId))) continue
    if (getScopedClosure(rules, permission).has(targetKey)) continue
    if (isScopedPermissionEffective(rules, permission, remaining.values())) continue
    add.set(key, permission)
    remaining.set(key, permission)
  }
  return { add, remove }
}

/** Action-only compatibility helpers. Callers should pass a scope when determining implications. */
function scopedActionRules(rules: ImplicationRule[], scope?: Omit<ScopedPermission, 'actionId'>) {
  return scope
    ? rules.filter((rule) => rule.bind_source !== 'from_result')
    : rules.filter(
        (rule) =>
          rule.bind_source === 'preserve' &&
          rule.bind_target === 'preserve' &&
          rule.from_tgt_type === null &&
          rule.from_res_type === null &&
          rule.to_tgt_type === null &&
          rule.to_res_type === null
      )
}

export function getImpliedActionIds(
  rules: ImplicationRule[],
  actionId: string,
  scope?: Omit<ScopedPermission, 'actionId'>
): Set<string> {
  if (scope)
    return new Set(
      [...getScopedClosure(rules, { actionId, ...scope }).values()]
        .map((item) => item.actionId)
        .filter((id) => id !== actionId)
    )
  const seen = new Set<string>([actionId]),
    stack = [actionId]
  while (stack.length) {
    const from = stack.pop()!
    for (const rule of scopedActionRules(rules))
      if (rule.from_act_id === from && !seen.has(rule.to_act_id)) {
        seen.add(rule.to_act_id)
        stack.push(rule.to_act_id)
      }
  }
  seen.delete(actionId)
  return seen
}

export function getImplyingActionIds(
  rules: ImplicationRule[],
  actionId: string,
  scope?: Omit<ScopedPermission, 'actionId'>
): Set<string> {
  const candidates = new Set(rules.map((rule) => rule.from_act_id))
  return new Set(
    [...candidates].filter(
      (id) =>
        id !== actionId &&
        (scope
          ? getScopedClosure(rules, { actionId: id, ...scope }).has(
              scopedPermissionKey({ actionId, ...scope })
            )
          : getImpliedActionIds(rules, id).has(actionId))
    )
  )
}

export function isActionEffective(
  rules: ImplicationRule[],
  actionId: string,
  hasExplicitAction: (id: string) => boolean,
  scope?: Omit<ScopedPermission, 'actionId'>
): boolean {
  return (
    hasExplicitAction(actionId) ||
    [...getImplyingActionIds(rules, actionId, scope)].some(hasExplicitAction)
  )
}

export function getActionIdsToRevoke(
  rules: ImplicationRule[],
  actionId: string,
  scope?: Omit<ScopedPermission, 'actionId'>
): Set<string> {
  return new Set([actionId, ...getImplyingActionIds(rules, actionId, scope)])
}

export function getActionToggleChanges(
  rules: ImplicationRule[],
  actionId: string,
  explicitActionIds: ReadonlySet<string>,
  allowedActionIds?: ReadonlySet<string>,
  scope?: Omit<ScopedPermission, 'actionId'>
): { add: Set<string>; remove: Set<string> } {
  const allowed =
    allowedActionIds ??
    new Set([
      actionId,
      ...explicitActionIds,
      ...rules.flatMap((rule) => [rule.from_act_id, rule.to_act_id]),
    ])
  const effective = (id: string, explicit = explicitActionIds) =>
    isActionEffective(rules, id, (candidate) => explicit.has(candidate), scope)
  if (!effective(actionId)) return { add: new Set([actionId]), remove: new Set() }
  const remove = new Set(
    [...getActionIdsToRevoke(rules, actionId, scope)].filter((id) => allowed.has(id))
  )
  const remaining = new Set([...explicitActionIds].filter((id) => !remove.has(id)))
  const add = new Set<string>()
  for (const id of allowed) {
    if (remove.has(id) || !effective(id) || effective(id, remaining)) continue
    remaining.add(id)
    add.add(id)
  }
  return { add, remove }
}

/** Evaluate a checkbox against all explicit scopes owned by the same principal. */
export function getPermissionToggleChanges(
  rules: ImplicationRule[],
  target: ScopedPermission,
  explicit: ReadonlyMap<string, ScopedPermission>,
  allowed?: ReadonlySet<string>
): { add: Map<string, ScopedPermission>; remove: Set<string> } {
  return getScopedToggleChanges(rules, target, explicit, allowed)
}

export function scopesForActions(
  actions: Iterable<string>,
  scope: Omit<ScopedPermission, 'actionId'>
): Map<string, ScopedPermission> {
  const map = new Map<string, ScopedPermission>()
  for (const actionId of actions) {
    const permission = { actionId, ...scope }
    map.set(scopedPermissionKey(permission), permission)
  }
  return map
}

/** Compute transitive effective permissions once per explicit assignment change. */
export function getEffectiveScopedPermissions(
  rules: ImplicationRule[],
  explicit: Iterable<ScopedPermission>
): Set<string> {
  const effective = new Set<string>()
  const byAction = new Map<string, ImplicationRule[]>()
  for (const rule of rules) {
    const matches = byAction.get(rule.from_act_id) ?? []
    matches.push(rule)
    byAction.set(rule.from_act_id, matches)
  }
  const stack = [...explicit]
  while (stack.length) {
    const current = stack.pop()!
    const key = scopedPermissionKey(current)
    if (effective.has(key)) continue
    effective.add(key)
    for (const rule of byAction.get(current.actionId) ?? []) {
      const implied = applyRule(rule, current)
      if (implied) stack.push(implied)
    }
  }
  return effective
}
